import { Container, Graphics } from 'pixi.js';
import type { CarSlot, RacePhase, Scores, Vec2 } from '@nanocars/shared';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../scenes/SceneManager';
import { COLORS, colourForSlot } from '../ui/theme';
import { makeLabel } from '../ui/widgets';

const PIP_ROW_HEIGHT = 30;
const PIP_MARGIN = 16;
const GO_DISPLAY_S = 0.6;
const FLASH_HZ = 4;

export interface ChevronWarning {
  colour: number;
  screenPos: Vec2;
  angle: number; // radians, direction the chevron points (toward the car)
  flashing: boolean;
}

export interface HudState {
  slots: readonly CarSlot[];
  scores: Scores;
  phase: RacePhase;
  phaseTime: number;
  countdownLength: number;
  roundWinner: number | null;
  ownLap: number;
  lapLimit: number;
  wrongWay: boolean;
  chevrons: readonly ChevronWarning[];
}

// Screen-space race HUD: score pips, lap counter, centre countdown/GO,
// wrong-way arrow, round-end banner and off-screen chevrons. Pure "draw
// what you're told" component - RaceScene computes anything that needs
// world/camera knowledge (chevron screen positions in particular) and hands
// this class plain screen-space data every frame.
export class Hud {
  readonly container = new Container();

  private readonly pips = new Container();
  private readonly lapLabel = makeLabel('', { fontSize: 20, fontWeight: '800', fill: COLORS.textLight });
  private readonly countdownLabel = makeLabel('', {
    fontSize: 96,
    fontWeight: '900',
    fill: COLORS.textLight,
  });
  private readonly wrongWayLabel = makeLabel('WRONG WAY', {
    fontSize: 22,
    fontWeight: '900',
    fill: COLORS.accentRed,
  });
  private readonly bannerLabel = makeLabel('', { fontSize: 30, fontWeight: '900', fill: COLORS.textLight });
  private readonly chevronLayer = new Container();

  private goTimer = 0;
  private prevPhase: RacePhase = 'countdown';
  private clock = 0;

  constructor() {
    this.lapLabel.anchor.set(1, 0);
    this.lapLabel.x = LOGICAL_WIDTH - PIP_MARGIN;
    this.lapLabel.y = PIP_MARGIN;

    this.countdownLabel.anchor.set(0.5);
    this.countdownLabel.x = LOGICAL_WIDTH / 2;
    this.countdownLabel.y = LOGICAL_HEIGHT / 2 - 40;

    this.wrongWayLabel.anchor.set(0.5);
    this.wrongWayLabel.x = LOGICAL_WIDTH / 2;
    this.wrongWayLabel.y = LOGICAL_HEIGHT - 100;

    this.bannerLabel.anchor.set(0.5);
    this.bannerLabel.x = LOGICAL_WIDTH / 2;
    this.bannerLabel.y = 120;

    this.container.addChild(this.pips, this.lapLabel, this.countdownLabel, this.wrongWayLabel, this.bannerLabel, this.chevronLayer);
  }

  update(state: HudState, dt: number): void {
    this.clock += dt;
    if (this.goTimer > 0) this.goTimer -= dt;
    if (this.prevPhase === 'countdown' && state.phase === 'racing') this.goTimer = GO_DISPLAY_S;
    this.prevPhase = state.phase;

    this.renderPips(state.slots, state.scores);
    this.lapLabel.text = `Lap ${Math.min(state.ownLap + 1, state.lapLimit)}/${state.lapLimit}`;

    this.updateCountdown(state);
    this.updateWrongWay(state.wrongWay);
    this.updateBanner(state);
    this.updateChevrons(state.chevrons);
  }

  private renderPips(slots: readonly CarSlot[], scores: Scores): void {
    // Destroy rather than detach: this runs every frame, so plain
    // removeChildren left a whole row (Container + Graphics + two Text) per
    // car orphaned on every single one.
    for (const child of this.pips.removeChildren()) child.destroy({ children: true });
    slots.forEach((slot, i) => {
      const row = new Container();
      row.x = PIP_MARGIN;
      row.y = PIP_MARGIN + i * PIP_ROW_HEIGHT;

      const swatch = new Graphics().circle(10, 10, 9).fill(colourForSlot(slot.colour));
      swatch.stroke({ width: 2, color: COLORS.outline });
      row.addChild(swatch);

      const name = makeLabel(slot.name, { fontSize: 16, fontWeight: '700', fill: COLORS.textLight });
      name.x = 26;
      name.y = 1;
      row.addChild(name);

      const score = makeLabel(String(scores[slot.id] ?? 0), { fontSize: 16, fontWeight: '900', fill: COLORS.accentYellow });
      score.x = 26 + name.width + 10;
      score.y = 1;
      row.addChild(score);

      this.pips.addChild(row);
    });
  }

  private updateCountdown(state: HudState): void {
    if (state.phase === 'countdown') {
      const remaining = state.countdownLength - state.phaseTime;
      this.countdownLabel.text = remaining > 0.5 ? String(Math.ceil(remaining)) : 'GO!';
      this.countdownLabel.visible = true;
    } else if (this.goTimer > 0) {
      this.countdownLabel.text = 'GO!';
      this.countdownLabel.visible = true;
    } else {
      this.countdownLabel.visible = false;
    }
  }

  private updateWrongWay(wrongWay: boolean): void {
    if (!wrongWay) {
      this.wrongWayLabel.visible = false;
      return;
    }
    this.wrongWayLabel.visible = Math.sin(this.clock * FLASH_HZ * Math.PI * 2) > 0;
  }

  private updateBanner(state: HudState): void {
    if (state.phase !== 'roundEnd' || state.roundWinner === null) {
      this.bannerLabel.visible = false;
      return;
    }
    const winner = state.slots.find((s) => s.id === state.roundWinner);
    this.bannerLabel.text = `${winner?.name ?? 'Someone'} takes the point`;
    this.bannerLabel.visible = true;
  }

  private updateChevrons(chevrons: readonly ChevronWarning[]): void {
    // Destroy rather than detach: this runs every frame, so plain
    // removeChildren left one orphaned Graphics per chevron per frame.
    for (const child of this.chevronLayer.removeChildren()) child.destroy();
    for (const c of chevrons) {
      if (c.flashing && Math.sin(this.clock * FLASH_HZ * Math.PI * 2) <= 0) continue;
      const g = new Graphics();
      g.poly([12, 0, -8, -9, -8, 9]).fill(c.colour);
      g.stroke({ width: 2, color: COLORS.outline });
      g.rotation = c.angle;
      g.position.set(c.screenPos.x, c.screenPos.y);
      this.chevronLayer.addChild(g);
    }
  }

  // Destroys the whole HUD subtree (pips, labels, chevrons). Called from
  // RaceScene.exit(); the Hud is not reused after this.
  destroy(): void {
    this.container.destroy({ children: true });
  }
}
